export type PostBlock = {
  id: string;
  type: 'paragraph' | 'heading1' | 'heading2' | 'quote' | 'image' | 'code';
  text?: string;
  src?: string;
};

export type PostPage = { title?: string; blocks: PostBlock[] };

export type PostModel = {
  title: string;
  excerpt?: string;
  cover?: string;
  commentsEnabled?: boolean;
  // Legacy single-page content
  blocks?: PostBlock[];
  // New multi-page content
  pages?: PostPage[];
};

const BASES = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? ['http://localhost:8000']
  : [''];

async function tryFetch(path: string, init?: RequestInit) {
  let last: any = null;
  for (const b of BASES) {
    try {
      const url = b ? `${b}${path}` : path;
      const res = await fetch(url, init);
      if (res.ok) return res;
      last = new Error(`HTTP ${res.status}`);
    } catch (e) { last = e; }
  }
  throw last || new Error('Network error');
}

async function parseJson(res: Response) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) return await res.json();
  const text = await res.text().catch(() => '');
  const snippet = text ? text.slice(0, 200) : '';
  throw new Error(`Expected JSON but got ${ct || 'unknown content-type'}${snippet ? `: ${snippet}` : ''}`);
}

export async function savePost(slug: string, post: PostModel) {
  const body = JSON.stringify({ slug, post });
  const res = await tryFetch('/save_post.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
  return parseJson(res);
}

export async function getPost(slug: string): Promise<PostModel | null> {
  try {
    const res = await tryFetch(`/get_post.php?slug=${encodeURIComponent(slug)}`);
    const data = await parseJson(res);
    return data.post as PostModel;
  } catch { return null; }
}

export async function listPosts(): Promise<{ id?: string; slug: string; title?: string; size: number; mtime: number }[]> {
  const res = await tryFetch('/list_posts.php');
  const data = await parseJson(res);
  return data.items || [];
}

export async function deletePost(slug: string): Promise<{ ok: boolean }>{
  const body = JSON.stringify({ slug });
  const res = await tryFetch('/delete_post.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
  return parseJson(res);
}

export async function postExists(slug: string): Promise<boolean> {
  try {
    const res = await tryFetch(`/get_post.php?slug=${encodeURIComponent(slug)}`);
    if (!res.ok) return false;
    const data = await parseJson(res);
    return !!data && !!data.post;
  } catch {
    return false;
  }
}
